/// <amd-module name="Feed/Legacy/Components/PackageVersionsList" />
import * as React from "react";

import { IconButton } from "OfficeFabric/Button";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import {
    CheckboxVisibility,
    ColumnActionsMode,
    DetailsRow,
    IColumn,
    IDetailsRowProps,
    SelectionMode
} from "OfficeFabric/DetailsList";
import { IconType } from "OfficeFabric/Icon";
import { Selection } from "OfficeFabric/Selection";
import { BaseComponent, IBaseProps } from "OfficeFabric/Utilities";

import { announce } from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import * as Utils_Url from "VSS/Platform/Util/Url";
import { Action } from "VSS/Reflux/Action";

import { Link } from "VSSUI/Link";
import { TooltipHost } from "VSSUI/Tooltip";
import * as Utils_Date from "VSSUI/Utilities/Date";
import { Filter } from "VSSUI/Utilities/Filter";
import { VssDetailsList, VssDetailsListTitleCell } from "VSSUI/VssDetailsList";

import { CiConstants, PackageDetailsPivot, PackageFilterBarConstants } from "Feed/Common/Constants/Constants";
import {
    MinimalPackageVersion,
    Package,
    PackageVersion,
    PackageVersionMetrics,
    RecycleBinPackageVersion
} from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { MetricType } from "Feed/Common/Types/MetricType";
import * as Actions from "Feed/Legacy/Actions/Actions";
import { IPackageVersionSelectedPayload } from "Feed/Legacy/Common/ActionPayloads";
import * as ContextMenuNavigationCommands from "Feed/Legacy/Common/ContextMenuNavigationCommands";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import { LoadingContainer } from "Feed/Legacy/Components/LoadingContainer";
import { IMetricProps, Metrics } from "Feed/Legacy/Components/Metric";
import { NoResultsPane } from "Feed/Legacy/Components/NoResultsPane";
import { PackageUpstreamSource } from "Feed/Legacy/Components/PackageUpstreamSource";
import { ViewsGridCell } from "Feed/Legacy/Components/ViewsGridCell";
import * as CommandGetters from "Feed/Legacy/Helpers/CommandGetters";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import * as PackageCommandMenuItem from "Feed/Legacy/Helpers/PackageCommandToContextMenuItem";
import { isPublicFeed } from "Feed/Legacy/Helpers/ProjectHelper";
import { getPackageDetailsPageUrl } from "Feed/Legacy/Helpers/UrlHelper";
import { IPackageProtocol } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { MavenKey } from "Feed/Legacy/Protocols/Maven/Constants/MavenConstants";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { copyToClipboard } from "Feed/Legacy/Utils/CopyToClipboard";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

import { HubWebPageDataService } from "../DataServices/WebPageDataService";

export interface IPackageVersionsListProps extends IBaseProps {
    pageContext: IVssPageContext;
    selectedPackage: Package;
    selectedVersion?: PackageVersion;
    feed: Feed;
    items: MinimalPackageVersion[];
    protocolMap: { [key: string]: IPackageProtocol };
    displayVersionDetails: (changingVersion: boolean) => void;
    selectedFeed: Feed;
    filterState?: Filter;
    isRecycleBin: boolean;
    actions: { [key: string]: Action<{}> };
    clearSelection?: boolean;
    isLoading: boolean;
    metricsEnabled: boolean;
    /* package version id maps to package version metrics */
    packageVersionMetricsMap: { [key: string]: PackageVersionMetrics };
}

export interface IPackageVersionsListState {
    wasCopied?: boolean;
    /**
     * Store selected versions, so when filtering changes (details list will reset selection)
     * and items in the grid changes
     * We can select those versions that were selected before filtering
     */
    selectedVersions: PackageVersion[];
}

export class PackageVersionsList extends BaseComponent<IPackageVersionsListProps, IPackageVersionsListState> {
    private _selection: Selection;
    private _isProjectScopedUpstreamsEnabled: boolean;

    constructor(props: IPackageVersionsListProps) {
        super(props);
        this.state = {
            wasCopied: false,
            selectedVersions: []
        };
        var webPageDataService = props.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
        this._isProjectScopedUpstreamsEnabled = webPageDataService.isProjectScopedUpstreamsEnabled();
        this._selection = new Selection({
            getKey: item => this._getSelectionKey(item as PackageVersion),
            onSelectionChanged: () => this._onSelectionChangedCallback(),
            canSelectItem: item => this._canSelectItem(item)
        });
    }

    public componentDidMount(): void {
        // Ensure that the Package Store has retrieved package versions (or initiate the retrieval)
        // Do this on a delay, because until this method has completed, the component hasn't
        // subscribed to store events (and any store changes emited from within
        // the action handler won't be received by this component)
        this._async.setImmediate(() => this.props.actions.VersionsPivotSelected.invoke({}));
    }

    public componentWillReceiveProps(nextProps: IPackageVersionsListProps): void {
        if (this.props.clearSelection === false && nextProps.clearSelection === true) {
            // Clear all selections when selectedVersions were cleared out in the package store (e.g. after deleting a version).
            this._selection.setAllSelected(false);
        }
    }

    public render(): JSX.Element {
        return (
            <LoadingContainer isLoading={this.props.isLoading}>
                {this.props.items && this.props.items.length > 0 ? (
                    <VssDetailsList
                        ariaLabelForSelectionColumn={PackageResources.DetailsList_SelectionColumn_AriaLabel}
                        ariaLabelForSelectAllCheckbox={PackageResources.DetailsList_SelectAll_AriaLabel}
                        checkButtonAriaLabel={PackageResources.DetailsList_Select_AriaLabel}
                        setKey={this.props.selectedPackage.id}
                        className={"package-version-list"}
                        allocateSpaceForActionsButtonWhileHidden={true}
                        actionsColumnKey={"version"}
                        getMenuItems={(item: PackageVersion) => this._getContextMenuItems(item)}
                        shouldDisplayActions={this._shouldDisplayActions}
                        items={this.props.items}
                        columns={this._getColumns()}
                        onRenderRow={this._onRenderRow}
                        selection={this._selection}
                        selectionMode={SelectionMode.multiple}
                        selectionPreservedOnEmptyClick={true}
                        checkboxVisibility={CheckboxVisibility.onHover}
                    />
                ) : (
                        <NoResultsPane
                            pageContext={this.props.pageContext}
                            header={PackageResources.PackageGrid_NoPackagesMessage}
                            subheader={""}
                            link={""}
                            linkText={""}
                            iconClass={"bowtie-search"}
                        />
                    )}
            </LoadingContainer>
        );
    }

    private _canSelectItem(item): boolean {
        return !item.isDeleted || this.props.isRecycleBin;
    }

    private _onRenderRow = (props: IDetailsRowProps) => {
        return (
            <div className={"versions-list-row"}>
                <DetailsRow {...props} />
            </div>
        );
    };

    private _shouldDisplayActions = (item: PackageVersion) => {
        if (this.props.isRecycleBin) {
            return false;
        }

        const commands: IContextualMenuItem[] = this._getContextMenuItems(item);
        if (commands.length > 0) {
            return true;
        }

        return false;
    };

    private _getContextMenuItems(item: PackageVersion): IContextualMenuItem[] {
        if (this.props.isRecycleBin) {
            return [];
        }

        const contextItemsGetter = new PackageCommandMenuItem.ContextualMenuItemsGetter();
        let menuItems: IContextualMenuItem[] = [];
        let commands: IPackageCommand[] = [];
        const changingVersion = item !== this.props.selectedVersion;

        // if multiple packages were selected, determine if a selected package was clicked
        if (
            this.state.selectedVersions &&
            this.state.selectedVersions.length > 1 &&
            this._selection.isKeySelected(item.id)
        ) {
            if (!item.isDeleted) {
                commands = this.props.protocolMap[
                    this.props.selectedPackage.protocolType
                ].getMultiSelectPackageCommands(this.props.selectedFeed, null, this.state.selectedVersions);
                menuItems = contextItemsGetter.GetContextMenuItems(commands);
            }
        } else {
            const packageCommandsGetter = new CommandGetters.PackageCommandsGetter();
            const viewFilterName = this._getViewName();
            menuItems = ContextMenuNavigationCommands.getNavigationCommands(
                this.props.pageContext,
                viewFilterName ? this.props.feed.name + "@" + viewFilterName : this.props.feed.name,
                this.props.selectedPackage,
                this.props.actions,
                item,
                () => this.props.displayVersionDetails(changingVersion),
                viewFilterName
            );

            menuItems.push({
                key: "link",
                name: PackageResources.VersionList_GetALink,
                iconProps: { iconName: "Link" },
                onClick: () => {
                    CustomerIntelligenceHelper.publishEvent(
                        this.props.pageContext,
                        CiConstants.PackageVersionLinkCopied,
                        {
                            protocol: this.props.selectedPackage.protocolType
                        }
                    );
                    const url = new Utils_Url.Uri(window.location.href);
                    url.addQueryParam("version", item.version, true);
                    url.addQueryParam("view", PackageDetailsPivot.OVERVIEW, true);

                    copyToClipboard(url.absoluteUri);
                }
            } as IContextualMenuItem);

            if (!item.isDeleted) {
                const isMaven = this._isMaven();
                menuItems.push({
                    key: "install",
                    title: isMaven
                        ? PackageResources.VersionList_CopyInstallCommand_Maven
                        : PackageResources.VersionList_CopyInstallCommand,
                    name: isMaven
                        ? PackageResources.VersionList_CopyInstallCommand_ContextMenu_Maven
                        : PackageResources.VersionList_CopyInstallCommand_ContextMenu,
                    iconProps: {
                        iconType: IconType.Default,
                        className: "bowtie-icon bowtie-file-type-cmd"
                    },
                    onClick: () => {
                        CustomerIntelligenceHelper.publishEvent(
                            this.props.pageContext,
                            CiConstants.PackageVersionInstallCommandCopied,
                            {
                                protocol: this.props.selectedPackage.protocolType
                            }
                        );
                        copyToClipboard(this._getCopyCommands(item));
                    }
                } as IContextualMenuItem);

                const packageProtocol = this.props.protocolMap[this.props.selectedPackage.protocolType];
                commands = packageCommandsGetter.getSingleSelectionItems(
                    this.props.selectedFeed,
                    packageProtocol,
                    this.props.selectedPackage,
                    item,
                    /*viaPackageList*/ false,
                    this.props.pageContext
                );

                const protocolContextMenuItems = contextItemsGetter.GetContextMenuItems(commands, item.version);
                menuItems.push(...protocolContextMenuItems);
            }
        }

        return menuItems;
    }

    private _getColumns(): IColumn[] {
        const columns: IColumn[] = [];
        columns.push({
            key: "version",
            fieldName: "version",
            name: PackageResources.VersionList_VersionColumn,
            minWidth: 200,
            maxWidth: 450,
            isResizable: true,
            headerClass: "version-header",
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item: PackageVersion) => {
                return this._getVersionColumn(item);
            }
        } as IColumn);

        // If protocol supports promote, show views column
        // tslint:disable:no-bitwise
        if (
            !this.props.isRecycleBin &&
            (this.props.protocolMap[this.props.selectedPackage.protocolType].supportedCommandsMask &
                ProtocolCommands.Promote) !==
            0
        ) {
            columns.push({
                key: "views",
                fieldName: "views",
                name: PackageResources.ReleaseViewsColumn,
                minWidth: 185,
                maxWidth: 185,
                isResizable: true,
                className: "views-grid-cell",
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: PackageVersion) => {
                    return <ViewsGridCell packageVersion={item} />;
                }
            } as IColumn);
        }

        if (this.props.isRecycleBin === true) {
            columns.push({
                key: "deletedDate",
                fieldName: "deletedDate",
                name: PackageResources.VersionList_DeletedColumn,
                isResizable: true,
                minWidth: 100,
                maxWidth: 150,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: PackageVersion) => {
                    const agoDate = Utils_Date.ago(item.deletedDate);
                    const fullDate = new Date(item.deletedDate).toLocaleDateString();
                    return (
                        <TooltipHost content={fullDate}>
                            <span>{agoDate}</span>
                        </TooltipHost>
                    );
                }
            } as IColumn);

            columns.push({
                key: "scheduledPermanentDeleteDate",
                fieldName: "scheduledPermanentDeleteDate",
                name: PackageResources.PermanentDeleteDateColumn,
                isResizable: true,
                minWidth: 100,
                maxWidth: 150,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: RecycleBinPackageVersion) => {
                    const displayDate = new Date(item.scheduledPermanentDeleteDate).toLocaleDateString();
                    return (
                        <TooltipHost content={displayDate}>
                            <span>{displayDate}</span>
                        </TooltipHost>
                    );
                }
            } as IColumn);
        } else {
            columns.push({
                key: "published",
                fieldName: "published",
                name: PackageResources.VersionList_PublishedColumn,
                isResizable: true,
                minWidth: 100,
                maxWidth: 150,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: PackageVersion) => {
                    const agoDate = Utils_Date.ago(item.publishDate);
                    const fullDate = new Date(item.publishDate).toLocaleDateString();
                    return (
                        <TooltipHost content={fullDate}>
                            <span>{agoDate}</span>
                        </TooltipHost>
                    );
                }
            } as IColumn);

            if (this.shouldDisplaySource(this.props.feed)) {
                columns.push({
                    key: "source",
                    fieldName: "source",
                    name: PackageResources.UpstreamSourceColumn,
                    isResizable: true,
                    minWidth: 100,
                    maxWidth: 150,
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: (item: PackageVersion) => {
                        return (
                            <PackageUpstreamSource
                                pageContext={this.props.pageContext}
                                packageSummary={this.props.selectedPackage}
                                packageVersion={item}
                                upstreamSources={this.props.selectedFeed.upstreamSources}
                            />
                        );
                    }
                } as IColumn);
            }

            if (this.props.metricsEnabled === true) {
                columns.push({
                    key: "downloads",
                    fieldName: "downloads",
                    name: PackageResources.PackageGrid_DownloadsColumn,
                    isResizable: false,
                    minWidth: 80,
                    maxWidth: 110,
                    columnActionsMode: ColumnActionsMode.disabled,
                    onRender: (item: PackageVersion) => {
                        const versionMetricsProps: IMetricProps = {
                            metrics: this.props.packageVersionMetricsMap[item.id],
                            metricType: MetricType.DownloadCount
                        };
                        return <Metrics {...versionMetricsProps} />;
                    }
                } as IColumn);

                if (!isPublicFeed(this.props.feed)) {
                    columns.push({
                        key: "users",
                        fieldName: "users",
                        name: PackageResources.PackageGrid_UsersColumn,
                        isResizable: false,
                        minWidth: 80,
                        maxWidth: 110,
                        columnActionsMode: ColumnActionsMode.disabled,
                        onRender: (item: PackageVersion) => {
                            const versionMetricsProps: IMetricProps = {
                                metrics: this.props.packageVersionMetricsMap[item.id],
                                metricType: MetricType.UniqueUsers
                            };
                            return <Metrics {...versionMetricsProps} />;
                        }
                    } as IColumn);
                }
            }
        }

        return columns;
    }

    private shouldDisplaySource(feed: Feed): boolean {
        if (feed.project != null) {
            if (!this._isProjectScopedUpstreamsEnabled) {
                return false;
            }
            if (isPublicFeed(feed)) {
                return false;
            }
        }
        return true;
    }

    private _getVersionColumn(item: PackageVersion): JSX.Element {
        return (
            <VssDetailsListTitleCell
                onRenderPrimaryText={() => {
                    const versionString =
                        !this.props.isRecycleBin &&
                            this.props.selectedVersion &&
                            this.props.selectedVersion.version === item.version
                            ? Utils_String.format(PackageResources.VersionList_SelectedVersionString, item.version)
                            : item.version;
                    const isMaven = this._isMaven();
                    const url =
                        getPackageDetailsPageUrl(
                            this.props.pageContext,
                            this.props.feed.name,
                            this.props.selectedPackage,
                            versionString
                        ) + "&view=overview";
                    let installCommand: string = null;
                    let content: string = null;

                    if (!isMaven) {
                        installCommand = this._getCopyCommands(item);
                        content = this.state.wasCopied
                            ? Utils_String.format(
                                PackageResources.VersionList_CopyInstallCommand_Copied,
                                installCommand
                            )
                            : PackageResources.VersionList_CopyInstallCommand;
                    }

                    return (
                        <div className={"version-column"}>
                            <Link
                                className={
                                    this.props.isRecycleBin || (item.isListed && !item.isDeleted)
                                        ? "version-link"
                                        : "version-link strike-through"
                                }
                                href={url}
                                disabled={this.props.isRecycleBin}
                                onClick={evt => {
                                    if (evt.ctrlKey) {
                                        return;
                                    }
                                    evt.preventDefault();
                                    this._onVersionClick(item, this.props.actions.PackageVersionSelected);
                                }}
                            >
                                {versionString}
                            </Link>
                            {!this.props.isRecycleBin &&
                                this.state.selectedVersions &&
                                this.state.selectedVersions.length < 2 &&
                                (installCommand || isMaven) &&
                                !item.isDeleted && (
                                    <TooltipHost
                                        content={isMaven ? null : content}
                                        // Maven needs new lines for the tooltip, so it needs to be rendered as JSX instead of a string.
                                        tooltipProps={
                                            isMaven
                                                ? {
                                                    onRenderContent: () => {
                                                        return (
                                                            <div>
                                                                {" "}
                                                                {this.state.wasCopied ? (
                                                                    this._getMavenToolTip(item)
                                                                ) : (
                                                                        <span>
                                                                            {
                                                                                PackageResources.VersionList_CopyInstallCommand_Maven
                                                                            }
                                                                        </span>
                                                                    )}{" "}
                                                            </div>
                                                        );
                                                    }
                                                }
                                                : null
                                        }
                                    >
                                        <IconButton
                                            className={"install-button"}
                                            onMouseOut={() => this.setState({ wasCopied: false })}
                                            iconProps={{
                                                iconType: IconType.Default,
                                                className: "bowtie-icon bowtie-file-type-cmd"
                                            }}
                                            onClick={() => this._onInstallCommandClick(item)}
                                            ariaLabel={
                                                isMaven
                                                    ? PackageResources.VersionList_CopyInstallCommand_ContextMenu_Maven
                                                    : PackageResources.VersionList_CopyInstallCommand_ContextMenu
                                            }
                                        />
                                    </TooltipHost>
                                )}
                        </div>
                    );
                }}
            />
        );
    }

    private _getCopyCommands(item: PackageVersion): string {
        const { feed, protocolMap, selectedPackage } = this.props;
        const packageProtocol = protocolMap[selectedPackage.protocolType];

        return packageProtocol.getCopyInstallCommand(feed, selectedPackage.name, item);
    }

    private _getMavenToolTip(item: PackageVersion): JSX.Element {
        const packageNameParts = this.props.selectedPackage.name ? this.props.selectedPackage.name.split(":") : "";

        if (packageNameParts.length === 2) {
            return (
                <ul style={{ margin: 0, padding: 0 }}>
                    <li>{"<dependency>"}</li>
                    <li>{"<groupId>" + packageNameParts[0] + "</groupId>"}</li>
                    <li>{"<artifactId>" + packageNameParts[1] + "</artifactId>"}</li>
                    <li>{"<version>" + item.version + "</version>"}</li>
                    <li>
                        {Utils_String.format(PackageResources.VersionList_CopyInstallCommand_Copied, "</dependency>")}
                    </li>
                </ul>
            );
        }

        return <span> {PackageResources.MavenOverview_InvalidPackageError} </span>;
    }

    private _onInstallCommandClick(item: PackageVersion): void {
        copyToClipboard(this._getCopyCommands(item));
        this.setState({
            wasCopied: true
        });

        const announcement: string = this._isMaven()
            ? PackageResources.VersionList_InstallCommand_Copied_AriaLabel_Maven
            : PackageResources.VersionList_InstallCommand_Copied_AriaLabel;
        announce(announcement);
    }

    private _onVersionClick(item: PackageVersion, packageVersionSelectedAction: Action<{}>): void {
        const changingVersion = item !== this.props.selectedVersion;
        const viewFilterName = this._getViewName();
        const payload = {
            version: item,
            viewName: viewFilterName
        } as IPackageVersionSelectedPayload;
        packageVersionSelectedAction.invoke(payload);
        this.props.displayVersionDetails(changingVersion);
    }

    private _getViewName(): string {
        const viewFilterValue = this.props.filterState.getFilterItemValue(PackageFilterBarConstants.ViewFilterKey);
        if (viewFilterValue == null || !viewFilterValue[0]) {
            return null;
        }

        const viewFilterName = viewFilterValue[0].split("@");
        return viewFilterName && viewFilterName.length === 1 ? viewFilterName[0] : viewFilterName[1];
    }

    private _getSelectionKey(version: PackageVersion): string {
        return version ? version.id : undefined;
    }

    private _onSelectionChangedCallback(): void {
        const selectedVersions = this._selection.getSelection() as PackageVersion[];

        // When filtering first time using keyword (action trigger is throttled by 200ms between keystrokes)
        // below action gets executed before Filter action completes and will throw action is executing error
        // so delay will invoke this action in next cycle
        this._async.setImmediate(() => Actions.VersionSelectionChanged.invoke(selectedVersions));
    }

    private _isMaven(): boolean {
        return this.props.selectedPackage.protocolType.toLowerCase() === MavenKey;
    }
}
